import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  partEquivalencesApi,
  type CreatePartEquivalenceInput,
} from './service';
import type { PartEquivalence } from './types';

/**
 * Query keys are parameterised by partId so different parts'
 * equivalence lists stay in cache without invalidating each other.
 * Mutations on equivalences invalidate BOTH endpoints of the pair
 * (partX and partY) so detail pages of either part stay consistent.
 */
export const partEquivalencesKeys = {
  all: ['part-equivalences'] as const,
  forPart: (partId: string) =>
    [...partEquivalencesKeys.all, 'for-part', partId] as const,
};

export function usePartEquivalences(partId: string | undefined) {
  return useQuery({
    queryKey: partId ? partEquivalencesKeys.forPart(partId) : partEquivalencesKeys.all,
    queryFn: () => partEquivalencesApi.listForPart(partId as string),
    enabled: Boolean(partId),
  });
}

export function useCreatePartEquivalence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: CreatePartEquivalenceInput) =>
      partEquivalencesApi.create(input),
    onSuccess: (created: PartEquivalence) => {
      // Invalidate both ends of the pair so any open detail page
      // re-fetches its equivalences list.
      qc.invalidateQueries({
        queryKey: partEquivalencesKeys.forPart(created.partXId),
      });
      qc.invalidateQueries({
        queryKey: partEquivalencesKeys.forPart(created.partYId),
      });
    },
  });
}

/**
 * Delete needs both partIds to invalidate correctly. The caller
 * passes them along with the equivalence id since the response
 * is 204 No Content.
 */
export function useDeletePartEquivalence() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
    }: {
      id: string;
      partXId: string;
      partYId: string;
    }) => partEquivalencesApi.remove(id),
    onSuccess: (_void, { partXId, partYId }) => {
      qc.invalidateQueries({
        queryKey: partEquivalencesKeys.forPart(partXId),
      });
      qc.invalidateQueries({
        queryKey: partEquivalencesKeys.forPart(partYId),
      });
    },
  });
}
